import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppLayout,
  TopNavigation,
  SideNavigation,
  SideNavigationProps,
  ButtonDropdownProps,
  Modal,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Box,
  Alert,
} from '@cloudscape-design/components';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { getConfig, getBuildInfo } from '../config';
import { canAccessBuilds } from '../utils/buildsAccess';
import { canAccessSyntheticData } from '../utils/syntheticAccess';
import { canViewDebugLog } from '../utils/debugAccess';
import { canReadDashboards } from '../utils/telemetryPermissions';
import { canAccessWorkflowTuning } from '../utils/workflowTuningAccess';
import { clearAttemptedLocation } from '../services/sessionRedirect';
import { AssistantProvider, useAssistant } from './assistant/AssistantProvider';
import AssistantDrawer from './assistant/AssistantDrawer';
import { ASSISTANT_DRAWER_ID, ASSISTANT_DRAWER_DEFAULT_SIZE } from './assistant/constants';

/**
 * Builds the items for the top-navigation settings dropdown based on the
 * user's role (portal-user-manager Requirements 1.1, 1.2).
 *
 * Exported as a pure function so the role gating is directly testable:
 * the `user-manager` item is present only for PortalAdmin users — it is
 * omitted entirely (not merely disabled) for every other role.
 */
export function buildSettingsDropdownItems(
  role: UserRole | undefined
): ButtonDropdownProps.ItemOrGroup[] {
  // A user whose resolved role is DataLabeler keeps only sign-out and
  // account settings (the change-password action) in the dropdown
  // (dda-data-labeling Requirement 2.2). Multi-role users resolve to a
  // different role and keep the full dropdown (Requirement 2.8).
  if (role === 'DataLabeler') {
    return [
      { id: 'change-password', text: 'Change Password' },
      { id: 'logout', text: 'Sign out' },
    ];
  }
  const isPortalAdmin = role === 'PortalAdmin';
  return [
    {
      id: 'profile',
      text: 'Profile',
      disabled: true,
    },
    {
      id: 'settings',
      text: 'Settings',
      // The settings page is PortalAdmin-only (same rule as the
      // sidebar link); other roles see the item disabled.
      disabled: !isPortalAdmin,
    },
    // The User Manager tool is PortalAdmin-only and, unlike the settings
    // item, is omitted entirely for other roles (Requirement 1.2).
    ...(isPortalAdmin
      ? [{ id: 'user-manager', text: 'User Manager' }]
      : []),
    {
      id: 'change-password',
      text: 'Change Password',
    },
    {
      id: 'logout',
      text: 'Sign out',
    },
  ];
}

/**
 * Builds the side-navigation item list for a user's role
 * (build-fleet-rbac-visibility Requirements 2.5, 2.6, 3.6).
 *
 * Exported as a pure function so the role gating is directly property-testable
 * (mirroring the `buildSettingsDropdownItems` pattern above). The list is the
 * historical navigation exactly, with one gating change: the
 * `{ text: 'Builds', href: '/builds' }` entry is included only when
 * `canAccessBuilds(role)`, so roles without builds access no longer see a link
 * to a page that can only render a 403 banner. The PortalAdmin-only group
 * (including "Build Fleet" → `/admin/fleet`) and the UseCaseAdmin audit-logs
 * handling are unchanged.
 */
export function buildNavigationItems(
  role: UserRole | undefined
): SideNavigationProps.Item[] {
  // A user whose resolved role is DataLabeler sees only the labeler
  // workspace destination (dda-data-labeling Requirement 2.2). Users who
  // hold DataLabeler alongside another role resolve to that other role and
  // keep its navigation unchanged (Requirement 2.8).
  if (role === 'DataLabeler') {
    return [
      { type: 'link' as const, text: 'My Labeling Tasks', href: '/labeler' },
    ];
  }

  // Base navigation items for all users
  const baseNavigationItems: SideNavigationProps.Item[] = [
    { type: 'link' as const, text: 'Dashboard', href: '/dashboard' },
    { type: 'link' as const, text: 'Use Cases', href: '/usecases' },
    { type: 'divider' as const },
    { type: 'link' as const, text: 'Data Management', href: '/data' },
    { type: 'link' as const, text: 'Labeling', href: '/labeling' },
    // The synthetic data generation workspace is limited to the roles
    // holding Data_Scientist_Access (DataScientist, UseCaseAdmin,
    // PortalAdmin) — synthetic-defect-data-generation Req 9.3, following
    // the Builds nav gating pattern below.
    ...(canAccessSyntheticData(role)
      ? [{ type: 'link' as const, text: 'Synthetic Data', href: '/synthetic' }]
      : []),
    { type: 'link' as const, text: 'Training', href: '/training' },
    { type: 'link' as const, text: 'Models', href: '/models' },
    { type: 'divider' as const },
    { type: 'link' as const, text: 'Workflows', href: '/workflows/builder' },
    // Workflow Tuning: a group of workflow-analysis tools, of which
    // VLM/LLM Anomaly Tuning is the first (quality-prompt-tuning Req 1.1).
    // Sits between "Workflows" and "Node Designer" and is limited to the
    // roles that may edit workflows, following the Builds nav gating
    // pattern below.
    ...(canAccessWorkflowTuning(role)
      ? [
          {
            type: 'expandable-link-group' as const,
            text: 'Workflow Tuning',
            href: '/workflow-tuning',
            items: [
              {
                type: 'link' as const,
                text: 'VLM/LLM Anomaly Tuning',
                href: '/workflow-tuning/anomaly',
              },
            ],
          },
        ]
      : []),
    // Node Designer: the plugin library plus the Use_Case Git connections
    // that plugin source is pushed to / pulled from
    // (custom-node-source-lifecycle Requirement 2.9: every role with
    // node-designer read may view connections; mutations are gated on
    // the page itself).
    {
      type: 'expandable-link-group' as const,
      text: 'Node Designer',
      href: '/node-designer',
      items: [
        {
          type: 'link' as const,
          text: 'Git connections',
          href: '/node-designer/git-connections',
        },
      ],
    },
    { type: 'link' as const, text: 'Components', href: '/components' },
    // The builds surface is limited to the roles holding `builds:*`
    // (DataScientist, UseCaseAdmin, PortalAdmin) — Req 2.5, 2.6.
    ...(canAccessBuilds(role)
      ? [{ type: 'link' as const, text: 'Builds', href: '/builds' }]
      : []),
    { type: 'link' as const, text: 'Deployments', href: '/deployments' },
    // The workflow telemetry dashboards are readable by every role holding
    // dashboards:read, i.e. every role but DataLabeler, which returned above
    // (workflow-telemetry-dashboards design §11.1, §11.4).
    ...(canReadDashboards(role)
      ? [{ type: 'link' as const, text: 'Workflow dashboards', href: '/workflow-dashboards' }]
      : []),
    { type: 'link' as const, text: 'Devices', href: '/devices' },
    // The workflow debug log (sessions and viewer) is readable by every
    // role holding VIEW_DEVICE_LOGS, i.e. every role but DataLabeler, which
    // returned above (workflow-debug-mode Requirements 2.6, 3.1).
    ...(canViewDebugLog(role)
      ? [{ type: 'link' as const, text: 'Debug log', href: '/debug' }]
      : []),
  ];

  // Admin-only items (PortalAdmin only)
  const portalAdminItems: SideNavigationProps.Item[] = [
    { type: 'divider' as const },
    { type: 'link' as const, text: 'Plugin Review', href: '/node-designer/review' },
    // Build server fleet management is PortalAdmin-only, like the
    // UserManager entry (portal-build-fleet-and-workflow-gates Req 6.1, 6.7).
    { type: 'link' as const, text: 'Build Fleet', href: '/admin/fleet' },
    { type: 'link' as const, text: 'Settings', href: '/settings' },
  ];

  // Audit logs - available to PortalAdmin and UseCaseAdmin
  const auditLogsItem: SideNavigationProps.Item = {
    type: 'link' as const,
    text: 'Audit Logs',
    href: '/audit'
  };

  // Combine navigation items based on user role
  const isPortalAdmin = role === 'PortalAdmin';
  const isUseCaseAdmin = role === 'UseCaseAdmin';

  if (isPortalAdmin) {
    return [...baseNavigationItems, ...portalAdminItems, auditLogsItem];
  }
  if (isUseCaseAdmin) {
    return [...baseNavigationItems, { type: 'divider' as const }, auditLogsItem];
  }
  return [...baseNavigationItems];
}

/**
 * The authenticated shell. The assistant provider wraps the layout so the
 * drawer's conversation and state survive route changes
 * (portal-ai-assistant Requirement 1.3).
 */
export default function Layout() {
  return (
    <AssistantProvider>
      <LayoutShell />
    </AssistantProvider>
  );
}

function LayoutShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, changePassword } = useAuth();
  const assistant = useAssistant();
  const config = getConfig();
  const branding = config.branding;
  const build = getBuildInfo();

  // The workflow builder needs the full horizontal space, so the side
  // navigation is collapsed by default on that route. The route only
  // provides the default: once the user toggles the navigation the
  // explicit choice wins for the rest of the session, whatever page
  // they navigate to.
  const isBuilderRoute = location.pathname.startsWith('/workflows/builder');
  const [navigationOverride, setNavigationOverride] = useState<boolean | null>(null);
  const navigationOpen = navigationOverride ?? !isBuilderRoute;

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [changePwError, setChangePwError] = useState('');
  const [changePwSuccess, setChangePwSuccess] = useState(false);

  const navigationItems = buildNavigationItems(user?.role);

  return (
    <>
      <TopNavigation
        identity={{
          href: '/',
          title: branding.applicationName,
          logo: branding.logoUrl ? {
            src: branding.logoUrl,
            alt: branding.companyName,
          } : undefined,
        }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: user?.email || user?.username || 'User',
            description: user?.role || '',
            iconName: 'user-profile',
            items: buildSettingsDropdownItems(user?.role),
            onItemClick: async ({ detail }) => {
              if (detail.id === 'logout') {
                await logout();
                // Deliberate sign-out is a clean start: forget any remembered
                // Attempted_Location before going to /login, so signing back
                // in lands on the default landing page
                // (portal-session-expiry-return-to-page Requirements 4.1,
                // 4.2). This runs *after* `logout()` on purpose — clearing
                // it first would let the re-render that `logout()` triggers
                // (isAuthenticated flips false while still on the protected
                // route) have `ProtectedRoute` record the location again.
                clearAttemptedLocation();
                navigate('/login');
              } else if (detail.id === 'settings') {
                navigate('/settings');
              } else if (detail.id === 'user-manager') {
                navigate('/admin/user-manager');
              } else if (detail.id === 'change-password') {
                setShowChangePassword(true);
                setChangePwError('');
                setChangePwSuccess(false);
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }
            },
          },
        ]}
      />
      <AppLayout
        // The workflow builder canvas needs the full content width: drop
        // the AppLayout content gutters and its default max content width
        // (the dead space between the side navigation and the node
        // palette) on the builder route only; the builder page applies
        // its own minimal internal padding. Every other page keeps the
        // default Cloudscape content paddings and width.
        disableContentPaddings={isBuilderRoute}
        maxContentWidth={isBuilderRoute ? Number.MAX_VALUE : undefined}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOverride(detail.open)}
        navigation={
          <SideNavigation
            activeHref={location.pathname}
            items={navigationItems}
            onFollow={(event) => {
              event.preventDefault();
              navigate(event.detail.href);
            }}
          />
        }
        content={<Outlet />}
        toolsHide
        navigationWidth={200}
        // The Portal assistant lives in an AppLayout drawer
        // (portal-ai-assistant design D6): Cloudscape owns hide/show, the
        // narrow-viewport overlay and resizing; the trigger in the drawer
        // bar is the collapsed control (Requirement 2).
        drawers={
          assistant.available
            ? [
                {
                  id: ASSISTANT_DRAWER_ID,
                  content: <AssistantDrawer />,
                  trigger: { iconName: 'gen-ai' },
                  badge: assistant.unread,
                  resizable: true,
                  defaultSize: ASSISTANT_DRAWER_DEFAULT_SIZE,
                  preserveInactiveContent: true,
                  ariaLabels: {
                    drawerName: 'Assistant',
                    triggerButton: 'Open the assistant',
                    closeButton: 'Hide the assistant',
                    resizeHandle: 'Resize the assistant',
                  },
                },
              ]
            : []
        }
        activeDrawerId={assistant.available && assistant.open ? ASSISTANT_DRAWER_ID : null}
        onDrawerChange={({ detail }) => assistant.setOpen(detail.activeDrawerId === ASSISTANT_DRAWER_ID)}
      />

      {/* Bottom version banner — shows the exact portal build that is running.
          Fixed to the viewport bottom so it persists across all pages. */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: '#232f3e',
          color: '#d1d5db',
          fontSize: '12px',
          lineHeight: '24px',
          height: '24px',
          padding: '0 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'monospace',
          borderTop: '1px solid #414d5c',
        }}
        title={`Build time: ${build.buildTime || 'unknown'}`}
      >
        <span>{branding.applicationName}</span>
        <span>
          {`v${build.version}`}
          {build.gitSha && build.gitSha !== 'unknown' ? ` · ${build.gitSha}` : ''}
          {build.buildTime ? ` · built ${build.buildTime.slice(0, 10)}` : ''}
        </span>
      </div>

      <Modal
        visible={showChangePassword}
        onDismiss={() => setShowChangePassword(false)}
        header="Change Password"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowChangePassword(false)}>Cancel</Button>
              <Button
                variant="primary"
                loading={changePwLoading}
                onClick={async () => {
                  setChangePwError('');
                  if (!oldPassword || !newPassword || !confirmPassword) {
                    setChangePwError('All fields are required');
                    return;
                  }
                  if (newPassword !== confirmPassword) {
                    setChangePwError('New passwords do not match');
                    return;
                  }
                  if (newPassword.length < 8) {
                    setChangePwError('Password must be at least 8 characters');
                    return;
                  }
                  try {
                    setChangePwLoading(true);
                    await changePassword(oldPassword, newPassword);
                    setChangePwSuccess(true);
                  } catch (err: any) {
                    setChangePwError(err.message || 'Failed to change password');
                  } finally {
                    setChangePwLoading(false);
                  }
                }}
              >
                Change Password
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {changePwError && <Alert type="error">{changePwError}</Alert>}
          {changePwSuccess && (
            <Alert type="success">Password changed successfully.</Alert>
          )}
          {!changePwSuccess && (
            <>
              <FormField label="Current Password">
                <Input
                  type="password"
                  value={oldPassword}
                  onChange={({ detail }) => setOldPassword(detail.value)}
                />
              </FormField>
              <FormField label="New Password">
                <Input
                  type="password"
                  value={newPassword}
                  onChange={({ detail }) => setNewPassword(detail.value)}
                />
              </FormField>
              <FormField label="Confirm New Password">
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={({ detail }) => setConfirmPassword(detail.value)}
                />
              </FormField>
            </>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
}
